import React, { createContext, useState } from 'react';

import { useFlags } from 'utils/react-context';
import { STATUS_RSVP_YES, STATUS_WAITLISTED } from 'common/schemas/sessionGuest';
import { SESSION_STATUS_CLOSED, SESSION_STATUS_OPEN } from 'common/schemas/session';
import { useSessionRegistration } from 'common/components/TouchpointEditor/hooks/useSessionRegistration';
import { usePreview } from 'common/components/TouchpointEditor/hooks/usePreview';
import { useGuestAccessHash } from 'utils/hooks/useGuestAccessHash';

export const UNREGISTER_SESSION_BUTTON_ID = 'unregisterSessionButton';
export const REGISTER_SESSION_BUTTON_ID = 'registerSessionButton';
export const UNREGISTER_WAITLIST_SESSION_BUTTON_ID = 'unregisterWaitlistSessionButton';
export const REGISTER_WAITLIST_SESSION_BUTTON_ID = 'registerWaitlistSessionButton';
export const ADD_TO_CALENDAR_BUTTON_ID = 'addToCalendarButton';
export const SESSION_LABEL_ID = 'statusLabel';
export const SESSION_REGISTRATION_CLOSED_LABEL_ID = 'registrationClosedLabel';

export const SessionRegistrationButtonsContext = createContext(null);

export const sessionStates = {
    REGISTERED: 'registered',
    REGISTERED_WAITLIST: 'registered-waitlist',
    NOT_REGISTERED: 'not-registered',
    NOT_REGISTERED_WAITLIST: 'not-registered-waitlist',
    REGISTRATION_CLOSED: 'registration-closed',
    AVAILABLE_TO_ADD_TO_CALENDAR: 'available-to-add-to-calendar',
    REGISTRATION_FLAG_DISABLED: 'registration-flag-disabled',
    LABEL_NEEDED: 'label-needed',
    NOT_SET: 'not-set',
};

export const PRE_EVENT_REGISTRATION = 'pre-event-registration';
export const POST_EVENT_REGISTRATION = 'post-event-registration';

/**
 * Manipulates the session object to simulate the properties it would have based on a given state. For example, if the
 * received state is NO_REGISTRATION_REQUIRED, the returned session object will contain
 * session.registrationRequired = false.
 * @param state The state for which the session properties should be simulated.
 * @param session The session object to be manipulated.
 * @returns {*|(*&{registrationRequired: boolean})} A session object with some properties changed to match the ones it
 * would have if the received state was in place. If the passed state does not require so, or it is not defined, the
 * session object will be returned untouched.
 */
function simulateSessionPropertiesByState(state, session) {
    switch (state) {
        case sessionStates.NOT_REGISTERED:
            return {
                ...session,
                ...(session.registrationRequired && { status: SESSION_STATUS_OPEN }),
            };
        case sessionStates.NOT_REGISTERED_WAITLIST:
        case sessionStates.REGISTERED_WAITLIST:
            return {
                ...session,
                ...(session.registrationRequired && { status: SESSION_STATUS_CLOSED }),
                waitlist: true,
            };
        case sessionStates.LABEL_NEEDED:
            return {
                ...session,
                ...(session.registrationRequired && { status: SESSION_STATUS_CLOSED }),
            };
        default:
            return session;
    }
}

function simulateGuestSessionRegistrationStatus(simulatedState, actualState, session) {
    if (session.registrationRequired) {
        switch (simulatedState) {
            case sessionStates.REGISTERED:
                return STATUS_RSVP_YES;
            case sessionStates.REGISTERED_WAITLIST:
                return STATUS_WAITLISTED;
            case sessionStates.AVAILABLE_TO_ADD_TO_CALENDAR:
                return STATUS_RSVP_YES;
            default:
                return actualState;
        }
    }
    return actualState;
}

/**
 * Provider that defines what session registration related buttons should be visible and which ones hidden.
 * This is useful to ensure the editor can show all the buttons when needed so that hosts can edit their styles without
 * having to simulate the real life scenario which would cause those buttons to show.
 * @param children
 * @returns {JSX.Element}
 * @constructor
 */
// eslint-disable-next-line react/prop-types
export const SessionRegistrationStateProvider = ({ children }) => {
    const [sessionRegistrationState, setSessionRegistrationState] = useState(sessionStates.NOT_SET);
    const [agendaPreviewState, setAgendaPreviewState] = useState(PRE_EVENT_REGISTRATION);

    return (
        <SessionRegistrationButtonsContext.Provider
            value={{
                sessionRegistrationState,
                setSessionRegistrationState,
                agendaPreviewState,
                setAgendaPreviewState,
            }}
        >
            {children}
        </SessionRegistrationButtonsContext.Provider>
    );
};

export const useSessionRegistrationButtonsStateMachine = () => React.useContext(SessionRegistrationButtonsContext);

// eslint-disable-next-line consistent-return
export const useSessionRegistrationElementsVisibility = ({ session, event, editor }) => {
    const { shouldPreview } = usePreview({ event });
    const { sessionRegistrationState, agendaPreviewState } = useSessionRegistrationButtonsStateMachine();
    const { guestSessionRegistrationStatuses, isSessionRegistrationReady } = useSessionRegistration();
    const { guestAccessHash } = useGuestAccessHash(`sessionsRegistration/${event?.id}/guestAccessHash`);
    const { isAgendaSessionRegistrationEnabled, isAgendaSessionRegistrationEnabledPhase2 } = useFlags();
    const sessionRegistrationStatus = guestSessionRegistrationStatuses?.[session.id];
    const sessionData = simulateSessionPropertiesByState(sessionRegistrationState, session);
    const guestRegistrationStatus = simulateGuestSessionRegistrationStatus(
        sessionRegistrationState,
        sessionRegistrationStatus,
        session,
    );
    let isGuestRegisteredForEvent = editor.editing ? true : !!guestAccessHash;
    if (shouldPreview) {
        isGuestRegisteredForEvent = agendaPreviewState !== PRE_EVENT_REGISTRATION;
    }

    const isGuestRegisteredForSession = guestRegistrationStatus === STATUS_RSVP_YES;
    const isGuestOnSessionWaitlist = guestRegistrationStatus === STATUS_WAITLISTED;

    const isSessionRegistrationClosed = sessionData.status === SESSION_STATUS_CLOSED;
    const isSessionWaitlistEnabled = sessionData.waitlist && isSessionRegistrationClosed;

    const visibleElements = [];

    if (!isAgendaSessionRegistrationEnabled || !isSessionRegistrationReady) {
        return visibleElements;
    }

    // No registration required label should be displayed only if the guest has a guest access hash.
    if (!sessionData.registrationRequired && isGuestRegisteredForEvent) {
        visibleElements.push(SESSION_LABEL_ID);
    }

    // Register session button
    if (
        sessionData.registrationRequired &&
        isGuestRegisteredForEvent &&
        !isGuestRegisteredForSession &&
        !isSessionRegistrationClosed
    ) {
        visibleElements.push(REGISTER_SESSION_BUTTON_ID);
    }

    // Unregister session button
    if (isGuestRegisteredForSession) {
        visibleElements.push(UNREGISTER_SESSION_BUTTON_ID);
    }

    // Add to calendar button
    if (isGuestRegisteredForEvent && (isGuestRegisteredForSession || !sessionData.registrationRequired)) {
        visibleElements.push(ADD_TO_CALENDAR_BUTTON_ID);
    }

    if (isAgendaSessionRegistrationEnabledPhase2) {
        // Registration closed label
        if (
            isSessionRegistrationClosed &&
            !isSessionWaitlistEnabled &&
            !isGuestRegisteredForSession &&
            !isGuestOnSessionWaitlist
        ) {
            visibleElements.push(SESSION_REGISTRATION_CLOSED_LABEL_ID);
        }

        // Register waitlist button
        if (
            sessionData.registrationRequired &&
            isGuestRegisteredForEvent &&
            !isGuestRegisteredForSession &&
            isSessionWaitlistEnabled &&
            !isGuestOnSessionWaitlist
        ) {
            visibleElements.push(REGISTER_WAITLIST_SESSION_BUTTON_ID);
        }
        // Unregister waitlist button
        if (isGuestOnSessionWaitlist) {
            visibleElements.push(UNREGISTER_WAITLIST_SESSION_BUTTON_ID);
        }
    }

    return visibleElements;
};
